// ==========================================================================
// CALENDAR & HOLIDAYS MODULE
// WuRuPayroll - Yearly/Monthly Calendar, Holidays, Special Workdays & Booking
// ==========================================================================

import {
  saveCalendar,
  getPublicHolidaysCache,
  savePublicHolidaysCache,
  getTimesheet,
  saveTimesheet,
  cleanUpOldHolidaysCache
} from '../firebase-db.js';

import { escapeHtml } from '../utils/sanitize.js';
import { toast, showConfirm } from '../utils/toast.js';
import { calculatePayrollForEmployee, getEmployeeWorkHoursStr } from './payroll-calc.js';

// Safe proxy for global application state
const state = new Proxy({}, {
  get(target, prop) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    return s[prop];
  },
  set(target, prop, value) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    s[prop] = value;
    return true;
  }
});

// Safe wrappers for app routing, context and permissions
const navigateTo = (page) => {
  if (typeof window !== 'undefined' && window.navigateTo) {
    return window.navigateTo(page);
  }
};

const loadCompanyContext = (forceLoadHolidays) => {
  if (typeof window !== 'undefined' && window.loadCompanyContext) {
    return window.loadCompanyContext(forceLoadHolidays);
  }
};

const checkPermission = (action) => {
  if (typeof window !== 'undefined' && window.checkPermission) {
    return window.checkPermission(action);
  }
  return true;
};

// ==========================================================================
// CALENDAR VIEW (Requirement 3, 25)
// ==========================================================================
function renderCalendarPage() {
  document.getElementById('page-display-title').textContent = 'ปฏิทินงาน';
  document.getElementById('page-display-desc').textContent = `วันทำงานพิเศษ วันหยุดประจำปี และชดเชยของบริษัท ${state.activeCompanySettings.name}`;

  const wrapper = document.getElementById('page-content-wrapper');

  wrapper.innerHTML = `
    <!-- Calendar Controls Card -->
    <div class="glass-card" style="margin-bottom: 25px;">
      <div class="calendar-view-header">
        <div style="display:flex; align-items:center; gap: 15px;">
          <h3 style="font-weight:700; color:var(--primary-dark);">ปฏิทินการทำงานประจำปี</h3>
          <input type="number" id="cal-year-selector" value="${state.currentCalendarYear}" style="width: 100px; padding: 6px 12px;" onchange="updateCalendarYear(this.value)">
        </div>
        
        <div style="display:flex; gap:10px;">
          <div class="calendar-modes">
            <button class="calendar-mode-btn" id="btn-mode-row" onclick="switchCalendarMode('row')">แถวตารางรายการ</button>
            <button class="calendar-mode-btn" id="btn-mode-monthly" onclick="switchCalendarMode('monthly')">รายเดือน</button>
            <button class="calendar-mode-btn active" id="btn-mode-yearly" onclick="switchCalendarMode('yearly')">รายปี (12 เดือน)</button>
          </div>
          <button class="btn btn-secondary" onclick="window.print()">🖨️ พิมพ์ประกาศรายปี (A4)</button>
          ${window.checkPermission('manage_holidays') ? `<button class="btn btn-primary" onclick="showAddHolidayModal()">➕ กำหนดวันหยุด/ทำงานพิเศษ</button>` : ''}
        </div>
      </div>
      
      <!-- Stats Count widgets & Color Legend (Requirement 3) -->
      <div style="display:flex; flex-wrap:wrap; gap: 20px; border-top: 1px solid var(--gray-300); padding-top:15px; margin-bottom: 10px; justify-content: flex-start; font-size: 0.95rem;">
        
        ${state.activeCompanySettings && state.activeCompanySettings.holidaySystem === 'employee_selected' ? '' : `
        <div style="display:flex; align-items:center; gap:8px;">
          <span style="width:14px; height:14px; border-radius:50%; background:var(--danger); display:inline-block; box-shadow: 0 1px 3px rgba(0,0,0,0.2);"></span>
          <span>วันหยุดประจำปี: <strong id="stat-holidays-count">...</strong> วัน</span>
        </div>
        `}

        ${state.activeCompanySettings && state.activeCompanySettings.holidaySystem === 'employee_selected' ? '' : `
        <div style="display:flex; align-items:center; gap:8px;">
          <span style="width:14px; height:14px; border-radius:50%; background:var(--accent); display:inline-block; box-shadow: 0 1px 3px rgba(0,0,0,0.2);"></span>
          <span>วันทำงานพิเศษ: <strong id="stat-special-count">...</strong> วัน</span>
        </div>
        `}

        ${state.activeCompanySettings && state.activeCompanySettings.holidaySystem === 'employee_selected' ? '' : `
        <div style="display:flex; align-items:center; gap:8px;">
          <span style="width:14px; height:14px; border-radius:50%; background:var(--success); display:inline-block; box-shadow: 0 1px 3px rgba(0,0,0,0.2);"></span>
          <span>วันหยุดชดเชย: <strong id="stat-comp-count">...</strong> วัน</span>
        </div>
        `}

        ${state.activeCompanySettings && state.activeCompanySettings.holidaySystem === 'employee_selected' ?
      state.employees.map((emp, idx) => {
        const colors = ['#8338ec', '#ff006e', '#fb5607', '#ffbe0b', '#3a86ff', '#2ec4b6', '#e71d36', '#011627'];
        const color = colors[idx % colors.length];
        const quota = state.activeCompanySettings.companyHolidaysQuota !== undefined ? state.activeCompanySettings.companyHolidaysQuota : 13;
        const used = state.employeeHolidays.filter(eh => eh.employeeId === emp.id && eh.date.startsWith(state.currentCalendarYear.toString())).length;
        return `
              <div style="display:flex; align-items:center; gap:8px;">
                <span style="width:14px; height:14px; border-radius:50%; background:${color}; display:inline-block; box-shadow: 0 1px 3px rgba(0,0,0,0.2);"></span>
                <span>วันหยุด ${escapeHtml(emp.nickname || emp.realName)} (${used}/${quota})</span>
              </div>
            `;
      }).join('')
      : ''}
        
        ${state.activeCompanySettings && state.activeCompanySettings.holidaySystem === 'employee_selected' ? '' : `
        <div style="display:flex; align-items:center; gap:8px; margin-left: auto; border-left: 1px solid var(--gray-300); padding-left: 20px;">
          <span>⏳ ชดเชยที่ค้าง: <strong id="stat-pending-comp-count" style="color:var(--danger);">...</strong> วัน</span>
        </div>
        `}
      </div>
    </div>

    <!-- Print Header (Hidden on screen, shown on print layout) -->
    <div class="print-header">
      <h1>ปฏิทินวันหยุดและวันปฏิบัติงานประจำปี พ.ศ. ${state.currentCalendarYear + 543}</h1>
      <h3>บริษัท ${escapeHtml(state.activeCompanySettings.name)}</h3>
    </div>

    <!-- Container where the actual views render -->
    <div id="calendar-render-box"></div>
  `;

  // Draw early default mode
  const activeMode = state.activeCalendarMode || 'yearly';
  switchCalendarMode(activeMode);
}

function updateCalendarYear(val) {
  state.currentCalendarYear = parseInt(val);
  if (state.selectedMonth) {
    const parts = state.selectedMonth.split('-');
    if (parts.length === 2) {
      state.selectedMonth = `${val}-${parts[1]}`;
    }
  }
  navigateTo('calendar');
};

function switchCalendarMode(mode) {
  state.activeCalendarMode = mode;
  const btnYearly = document.getElementById('btn-mode-yearly');
  const btnRow = document.getElementById('btn-mode-row');
  const btnMonthly = document.getElementById('btn-mode-monthly');
  const box = document.getElementById('calendar-render-box');
  if (!box) return;

  btnYearly?.classList.remove('active');
  btnRow?.classList.remove('active');
  btnMonthly?.classList.remove('active');

  if (mode === 'yearly') {
    btnYearly?.classList.add('active');
    renderYearlyCalendarGrid(box);
  } else if (mode === 'row') {
    btnRow?.classList.add('active');
    renderRowCalendarList(box);
  } else if (mode === 'monthly') {
    btnMonthly?.classList.add('active');
    renderMonthlyCalendarGrid(box);
  }

  // Update holiday statistics counters
  updateCalendarStatsCounters();
};

function updateCalendarStatsCounters() {
  const holidaysCount = state.holidays.length;

  // วันทำงานพิเศษ: นับเฉพาะที่ไม่ใช่ standalone (คือวันที่มาทำจริง)
  const specialCount = state.specialWorkdays.filter(s => !s.isStandalone).length;

  // วันหยุดชดเชย: นับทั้งหมดที่มี compensatoryOffDate (ทั้งที่ผูกกับวันทำงานพิเศษ และแบบ standalone)
  const compCount = state.specialWorkdays.filter(s => s.compensatoryOffDate).length;

  // ชดเชยที่ค้าง: คำนวณจาก วันทำงานพิเศษทั้งหมด - วันหยุดชดเชยทั้งหมดที่ใช้ไป (ไม่ให้ติดลบ)
  const pendingCompCount = Math.max(0, specialCount - compCount);

  let holidaysCountText = String(holidaysCount);
  if (state.activeCompanySettings && state.activeCompanySettings.holidaySystem === 'employee_selected') {
    holidaysCountText = `${holidaysCount} (พนักงานเลือกเอง: ${state.employeeHolidays.length})`;
  }

  // Update Main Counters
  const statHolidaysCountEl = document.getElementById('stat-holidays-count');
  const statSpecialCountEl = document.getElementById('stat-special-count');
  const statCompCountEl = document.getElementById('stat-comp-count');
  const statPendingCompCountEl = document.getElementById('stat-pending-comp-count');

  if (statHolidaysCountEl) statHolidaysCountEl.textContent = holidaysCountText;
  if (statSpecialCountEl) statSpecialCountEl.textContent = specialCount;
  if (statCompCountEl) statCompCountEl.textContent = compCount;
  if (statPendingCompCountEl) statPendingCompCountEl.textContent = pendingCompCount;

  // Update Top Legend Counters
  const statHolidaysEl = document.getElementById('stat-holidays');
  const statSpecialEl = document.getElementById('stat-special');
  const statCompEl = document.getElementById('stat-comp');

  if (statHolidaysEl) statHolidaysEl.textContent = holidaysCount;
  if (statSpecialEl) statSpecialEl.textContent = specialCount;
  if (statCompEl) statCompEl.textContent = compCount;
}

// Render row / list view
function renderRowCalendarList(container) {
  let listHTML = `<div class="glass-card" style="padding: 0;">`;

  // Combine all items into chronologically sorted list
  const allEvents = [];
  state.holidays.forEach(h => {
    allEvents.push({ date: h.date, name: h.name, type: 'holiday' });
  });
  if (state.activeCompanySettings && state.activeCompanySettings.holidaySystem === 'employee_selected') {
    state.employeeHolidays.forEach(eh => {
      allEvents.push({
        date: eh.date,
        name: `วันหยุดประจำปี: ${eh.employeeName}`,
        type: 'employee_holiday'
      });
    });
  }
  state.specialWorkdays.forEach(s => {
    allEvents.push({
      date: s.date,
      name: `ทำงานพิเศษ: ${s.name}`,
      type: 'special',
      compensatoryOffDate: s.compensatoryOffDate,
      workHours: s.workHours
    });
    if (s.compensatoryOffDate) {
      allEvents.push({
        date: s.compensatoryOffDate,
        name: `ชดเชยวันทำงานพิเศษ: ${s.name}`,
        type: 'comp'
      });
    }
  });

  allEvents.sort((a, b) => new Date(a.date) - new Date(b.date));

  if (allEvents.length === 0) {
    listHTML += `<div style="padding:40px; text-align:center; color:var(--text-sub);">ยังไม่มีการกำหนดวันสำคัญในปฏิทินปีนี้</div>`;
  } else {
    allEvents.forEach(evt => {
      const typeLabel = evt.type === 'holiday'
        ? `<span class="badge badge-danger">วันหยุดประจำปี</span>`
        : (evt.type === 'employee_holiday'
          ? `<span class="badge badge-success" style="background-color: #8338ec; color: var(--white);">วันหยุดลูกน้อง</span>`
          : (evt.type === 'special'
            ? `<span class="badge badge-warning">วันทำงานพิเศษ</span>`
            : `<span class="badge badge-success">วันหยุดชดเชย</span>`));

      const hoursHint = evt.type === 'special' && evt.workHours ? ` <span style="font-size:0.8rem; padding: 2px 6px; border-radius: 4px; background: rgba(0,0,0,0.05); color:var(--text-sub);">${evt.workHours} ชม.</span>` : '';

      listHTML += `
        <div class="calendar-row-item">
          <div class="calendar-date-lbl">${new Date(evt.date).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
          <div class="calendar-desc-lbl"><strong>${evt.name}</strong>${hoursHint}</div>
          <div>${typeLabel}</div>
          <div style="text-align:right; display:flex; gap:5px; justify-content:flex-end;">
            ${evt.type === 'employee_holiday' ? `
              <button class="btn btn-secondary btn-sm" onclick="showEmployeeHolidayBookingModal('${evt.date}')" style="padding:4px 8px;">📅 จัดการ</button>
            ` : (window.checkPermission('manage_holidays') ? `
              <button class="btn btn-secondary btn-sm" onclick="showEditHolidayModal('${evt.date}', '${evt.type}')" style="padding:4px 8px;">✏️ แก้ไข</button>
              <button class="btn btn-danger btn-sm" onclick="deleteCalendarEventTrigger('${evt.date}', '${evt.type}')" style="padding:4px 8px;">🗑️ ลบ</button>
            ` : '🔒')}
          </div>
        </div>
      `;
    });
  }

  listHTML += `</div>`;
  container.innerHTML = listHTML;
}

// Render yearly 12-month grid (Ideal for A4 declaration printout) (Requirement 3)
function renderYearlyCalendarGrid(container) {
  let gridHTML = `<div class="yearly-calendar-grid">`;
  const thaiMonths = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
  ];

  for (let m = 0; m < 12; m++) {
    // Generate dates grid for month m of current year
    const firstDay = new Date(state.currentCalendarYear, m, 1).getDay();
    const totalDays = new Date(state.currentCalendarYear, m + 1, 0).getDate();

    let daysGridHTML = '';

    // Draw weekday titles (Sun-Sat)
    const shortWeekdays = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'];
    shortWeekdays.forEach((wk, i) => {
      const isSat = i === 6;
      daysGridHTML += `<div class="calendar-day-header ${isSat ? 'is-saturday' : ''}" style="padding:2px 0; font-size:0.68rem; ${isSat ? 'border-radius:4px;' : ''}">${wk}</div>`;
    });

    // Blank cells before day 1
    for (let b = 0; b < firstDay; b++) {
      const isSat = b === 6;
      daysGridHTML += `<div class="${isSat ? 'is-saturday' : ''}" style="${isSat ? 'border-radius:4px;' : ''}"></div>`;
    }

    // Days numbers
    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${state.currentCalendarYear}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

      // Determine if date has event
      const isHoliday = state.holidays.some(h => h.date === dateStr);
      const isSpecial = state.specialWorkdays.some(s => s.date === dateStr);
      const isComp = state.specialWorkdays.some(s => s.compensatoryOffDate === dateStr);
      const empHols = (state.activeCompanySettings && state.activeCompanySettings.holidaySystem === 'employee_selected')
        ? state.employeeHolidays.filter(eh => eh.date === dateStr)
        : [];
      const hasEmpHoliday = empHols.length > 0;

      let eventClass = '';
      let titleHover = `${d} ${thaiMonths[m]}`;
      let clickType = 'none';
      let cellStyle = '';

      if (isHoliday) {
        eventClass = 'event-holiday has-event';
        titleHover += ` (${state.holidays.find(h => h.date === dateStr).name})`;
        clickType = 'holiday';
      } else if (isSpecial) {
        eventClass = 'event-special has-event';
        titleHover += ` (งานพิเศษ: ${state.specialWorkdays.find(s => s.date === dateStr).name})`;
        clickType = 'special';
      } else if (isComp) {
        eventClass = 'event-comp has-event';
        titleHover += ` (ชดเชยพิเศษ)`;
        clickType = 'comp';
      } else if (hasEmpHoliday) {
        eventClass = 'event-emp-holiday has-event';
        const colors = ['#8338ec', '#ff006e', '#fb5607', '#ffbe0b', '#3a86ff', '#2ec4b6', '#e71d36', '#011627'];

        if (empHols.length === 1) {
          const empIdx = state.employees.findIndex(e => e.id === empHols[0].employeeId);
          if (empIdx >= 0) {
            cellStyle = `style="background: ${colors[empIdx % colors.length]} !important;"`;
          }
        } else {
          let stops = [];
          empHols.forEach((eh, i) => {
            const empIdx = state.employees.findIndex(e => e.id === eh.employeeId);
            const color = empIdx >= 0 ? colors[empIdx % colors.length] : '#8338ec';
            const pct1 = (i / empHols.length) * 100;
            const pct2 = ((i + 1) / empHols.length) * 100;
            stops.push(`${color} ${pct1}%, ${color} ${pct2}%`);
          });
          cellStyle = `style="background: linear-gradient(135deg, ${stops.join(', ')}) !important;"`;
        }

        const empNames = empHols.map(eh => eh.employeeName).join(', ');
        titleHover += ` (วันหยุดลูกน้อง: ${empNames})`;
        clickType = 'employee_holiday';
      }

      const dayOfWeek = (firstDay + d - 1) % 7;
      if (!eventClass && dayOfWeek === 6) {
        eventClass = 'is-saturday';
      }

      daysGridHTML += `
        <div class="yearly-day-cell ${eventClass}" ${cellStyle} title="${titleHover}" onclick="showDayEventAction('${dateStr}', '${clickType}')">
          ${d}
        </div>
      `;
    }

    gridHTML += `
      <div class="yearly-month-card">
        <div class="yearly-month-title" style="cursor: pointer; transition: color 0.2s;" onmouseover="this.style.color='var(--primary)'" onmouseout="this.style.color=''" onclick="window.changeMonthlyMonth(${m}); window.switchCalendarMode('monthly');" title="คลิกเพื่อดูปฏิทินแบบรายเดือน">${thaiMonths[m]}</div>
        <div class="yearly-days-grid">${daysGridHTML}</div>
      </div>
    `;
  }

  gridHTML += `</div>`;
  container.innerHTML = gridHTML;
}

// --- New Monthly Calendar Functions ---
function getFetchedPublicHolidays() {
  if (!state.fetchedPublicHolidays) {
    state.fetchedPublicHolidays = {};
  }
  return state.fetchedPublicHolidays;
}

function changeMonthlyMonth(monthIndex) {
  let m = parseInt(monthIndex);
  if (m < 0) {
    state.currentMonthlyMonth = 11;
    window.updateCalendarYear(state.currentCalendarYear - 1);
    return;
  } else if (m > 11) {
    state.currentMonthlyMonth = 0;
    window.updateCalendarYear(state.currentCalendarYear + 1);
    return;
  }

  state.currentMonthlyMonth = m;
  const container = document.getElementById('calendar-render-box');
  if (container) {
    renderMonthlyCalendarGrid(container);
  }
};

async function fetchThaiPublicHolidays(year) {
  const holidaysMap = getFetchedPublicHolidays();
  if (holidaysMap[year]) {
    return holidaysMap[year];
  }

  const syncDays = state.activeCompanySettings?.publicHolidaySyncDays || 7;
  const syncMs = syncDays * 24 * 60 * 60 * 1000;

  // 1. Check Firebase Cache
  try {
    const cachedData = await getPublicHolidaysCache(year);
    if (cachedData && cachedData.lastUpdated) {
      const age = Date.now() - cachedData.lastUpdated;
      if (age <= syncMs) {
        state.fetchedPublicHolidays[year] = cachedData.holidays || [];
        return state.fetchedPublicHolidays[year];
      }
    }
  } catch (err) {
    console.error('Error reading holidays cache:', err);
  }

  // 2. Fetch from API if cache is old or missing
  let apiUrl = state.activeCompanySettings?.publicHolidayApiUrl || 'https://thailandformats.com/api/v1/holidays/{year}';
  apiUrl = apiUrl.replace('{year}', year);

  const fetchAndParse = async (urlToFetch) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    let response;
    try {
      response = await fetch(urlToFetch, { signal: controller.signal });
      clearTimeout(timeoutId);
    } catch (err) {
      clearTimeout(timeoutId);
      throw err;
    }

    if (!response.ok) throw new Error('API Error');

    const contentType = response.headers.get('content-type') || '';
    let normalized = [];

    if (contentType.includes('text/calendar') || apiUrl.toLowerCase().includes('.ics')) {
      // Parse ICS
      const icsText = await response.text();
      const lines = icsText.split(/\r?\n/);
      let currentEvent = null;
      for (let line of lines) {
        if (line.startsWith('BEGIN:VEVENT')) {
          currentEvent = {};
        } else if (line.startsWith('END:VEVENT')) {
          if (currentEvent && currentEvent.date && currentEvent.date.startsWith(year.toString())) {
            normalized.push(currentEvent);
          }
          currentEvent = null;
        } else if (currentEvent) {
          if (line.startsWith('DTSTART')) {
            const match = line.match(/:(\d{4})(\d{2})(\d{2})/);
            if (match) {
              currentEvent.date = `${match[1]}-${match[2]}-${match[3]}`;
            }
          } else if (line.startsWith('SUMMARY:')) {
            currentEvent.name = line.substring(8).trim();
            currentEvent.localName = currentEvent.name;
          }
        }
      }
      return normalized;
    } else {
      // Parse JSON
      let data = await response.json();
      if (Array.isArray(data)) {
        normalized = data; // Nager format
      } else if (data && data.holidays && Array.isArray(data.holidays)) {
        // thailandformats format
        normalized = data.holidays.map(h => ({
          date: h.start_date,
          localName: h.title, // or title_th if available
          name: h.title
        }));
      }
      return normalized;
    }
  };

  const saveAndReturn = async (data) => {
    getFetchedPublicHolidays()[year] = data;
    try {
      await savePublicHolidaysCache(year, data);
      cleanUpOldHolidaysCache(new Date().getFullYear()); // Fire and forget cleanup
    } catch (err) {
      console.error('Error saving holidays to cache:', err);
    }
    return data;
  };

  try {
    const data = await fetchAndParse(apiUrl);
    return await saveAndReturn(data);
  } catch (error) {
    console.warn('Direct fetch failed (possibly CORS), attempting proxy...', error);
    try {
      const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(apiUrl)}`;
      const data = await fetchAndParse(proxyUrl);
      return await saveAndReturn(data);
    } catch (proxyError) {
      console.error('Error fetching public holidays:', proxyError);

      // 3. Fallback: If both fail, use stale cache if it exists, otherwise empty array
      try {
        const cachedData = await getPublicHolidaysCache(year);
        if (cachedData && cachedData.holidays) {
          getFetchedPublicHolidays()[year] = cachedData.holidays;
          return cachedData.holidays;
        }
      } catch (e) { }

      return [];
    }
  }
}

async function renderMonthlyCalendarGrid(container) {
  if (state.currentMonthlyMonth === undefined) {
    if (state.selectedMonth) {
      state.currentMonthlyMonth = parseInt(state.selectedMonth.split('-')[1]) - 1;
    } else {
      state.currentMonthlyMonth = new Date().getMonth();
    }
  }

  const thaiMonths = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
  ];

  const holidaysMap = getFetchedPublicHolidays();
  let publicHolidays = holidaysMap[state.currentCalendarYear] || [];

  if (!holidaysMap[state.currentCalendarYear] && !state.isFetchingHolidays) {
    state.isFetchingHolidays = true;
    fetchThaiPublicHolidays(state.currentCalendarYear).then(() => {
      state.isFetchingHolidays = false;
      const currentContainer = document.getElementById('calendar-render-box');
      if (currentContainer && state.activePage === 'calendar') {
        renderMonthlyCalendarGrid(currentContainer);
      }
    }).catch(() => {
      state.isFetchingHolidays = false;
      const currentContainer = document.getElementById('calendar-render-box');
      if (currentContainer && state.activePage === 'calendar') {
        renderMonthlyCalendarGrid(currentContainer);
      }
    });
  }

  const loadingIndicator = state.isFetchingHolidays ?
    `<span style="margin-left: 15px; font-size: 0.85rem; color: var(--warning); display: inline-flex; align-items: center; font-weight: normal;">
      ⏳ กำลังซิงค์ข้อมูลวันหยุด API...
    </span>` : '';

  let gridHTML = `
    <div class="glass-card" style="padding: 20px;">
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom: 20px;">
        <h4 style="font-weight:700; color:var(--primary-dark); margin:0; display:flex; align-items:center;">
          ประจำเดือน:
          ${loadingIndicator}
        </h4>
        <div style="display:flex; align-items:center; gap: 8px;">
          <button class="btn btn-sm" style="padding: 6px 12px; background:var(--gray-200); border:none; border-radius:4px; cursor:pointer;" onclick="window.changeMonthlyMonth(${state.currentMonthlyMonth - 1})">◀</button>
          <select id="monthly-month-selector" style="padding: 8px 12px; border-radius: 4px; border: 1px solid var(--gray-300); font-family: inherit; cursor:pointer; min-width: 120px; text-align:center;" onchange="window.changeMonthlyMonth(this.value)">
            ${thaiMonths.map((mName, idx) => `<option value="${idx}" ${state.currentMonthlyMonth === idx ? 'selected' : ''}>${mName}</option>`).join('')}
          </select>
          <button class="btn btn-sm" style="padding: 6px 12px; background:var(--gray-200); border:none; border-radius:4px; cursor:pointer;" onclick="window.changeMonthlyMonth(${state.currentMonthlyMonth + 1})">▶</button>
        </div>
      </div>
      <div class="monthly-calendar-grid">
  `;

  const m = state.currentMonthlyMonth;
  const year = state.currentCalendarYear;
  const firstDay = new Date(year, m, 1).getDay();
  const totalDays = new Date(year, m + 1, 0).getDate();

  // Weekday Headers
  const shortWeekdays = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
  let daysGridHTML = `<div style="display:grid; grid-template-columns: repeat(7, 1fr); gap: 5px; text-align:center; font-weight:bold; margin-bottom: 5px;">`;
  shortWeekdays.forEach((wk, i) => {
    const isSat = i === 6;
    const isSun = i === 0;
    let color = 'var(--text-main)';
    if (isSat) color = '#8338ec';
    if (isSun) color = 'var(--danger)';
    daysGridHTML += `<div style="padding:10px 0; border-radius:4px; background:var(--gray-100); color:${color};">${wk}</div>`;
  });
  daysGridHTML += `</div>`;

  daysGridHTML += `<div style="display:grid; grid-template-columns: repeat(7, 1fr); gap: 5px; grid-auto-rows: minmax(100px, auto);">`;

  // Blank cells before day 1
  for (let b = 0; b < firstDay; b++) {
    daysGridHTML += `<div style="background:rgba(0,0,0,0.02); border-radius:4px;"></div>`;
  }

  for (let d = 1; d <= totalDays; d++) {
    const dateStr = `${year}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

    const isHoliday = state.holidays.find(h => h.date === dateStr);
    const isSpecial = state.specialWorkdays.find(s => s.date === dateStr);
    const isComp = state.specialWorkdays.find(s => s.compensatoryOffDate === dateStr);
    const empHols = (state.activeCompanySettings && state.activeCompanySettings.holidaySystem === 'employee_selected')
      ? state.employeeHolidays.filter(eh => eh.date === dateStr)
      : [];

    const pubHoliday = publicHolidays.find(ph => ph.date === dateStr);

    let cellContent = `<div style="text-align:right; font-weight:bold; padding: 5px;">${d}</div>`;
    let eventContent = `<div style="display:flex; flex-direction:column; gap:3px; padding:0 5px 5px 5px; font-size:0.75rem;">`;

    if (pubHoliday) {
      eventContent += `<div style="background:var(--gray-200); color:var(--text-sub); padding:3px 6px; border-radius:3px; line-height:1.2;">🏛️ ${pubHoliday.localName || pubHoliday.name}</div>`;
    }

    if (isHoliday) {
      eventContent += `<div style="background:rgba(239, 71, 111, 0.1); color:var(--danger); border:1px solid rgba(239, 71, 111, 0.3); padding:3px 6px; border-radius:3px; font-weight:bold;">${isHoliday.name}</div>`;
    }

    if (isSpecial) {
      eventContent += `<div style="background:rgba(255, 209, 102, 0.15); color:#d48a00; border:1px solid rgba(255, 209, 102, 0.4); padding:3px 6px; border-radius:3px; font-weight:bold;">⚡ ${isSpecial.name}</div>`;
    }

    if (isComp) {
      eventContent += `<div style="background:rgba(6, 214, 160, 0.1); color:var(--success); border:1px solid rgba(6, 214, 160, 0.3); padding:3px 6px; border-radius:3px; font-weight:bold;">🔄 ชดเชยพิเศษ</div>`;
    }

    if (empHols.length > 0) {
      const colors = ['#8338ec', '#ff006e', '#fb5607', '#ffbe0b', '#3a86ff', '#2ec4b6', '#e71d36', '#011627'];
      empHols.forEach(eh => {
        const empIdx = state.employees.findIndex(e => e.id === eh.employeeId);
        const color = empIdx >= 0 ? colors[empIdx % colors.length] : '#8338ec';
        eventContent += `<div style="background:${color}1a; color:${color}; border:1px solid ${color}4d; padding:3px 6px; border-radius:3px; font-weight:bold;">💜 วันหยุด ${eh.employeeName}</div>`;
      });
    }

    eventContent += `</div>`;

    const dayOfWeek = (firstDay + d - 1) % 7;
    let bgStyle = 'background:var(--card-bg); border:1px solid var(--gray-200);';
    if (dayOfWeek === 0) {
      bgStyle = 'background:rgba(0,0,0,0.01); border:1px solid var(--gray-200);';
    } else if (dayOfWeek === 6) {
      bgStyle = 'background:rgba(135, 206, 250, 0.2); border:1px solid var(--gray-200);'; // สีฟ้าอ่อนๆ สำหรับวันเสาร์
    }

    let clickType = 'none';
    if (isHoliday) clickType = 'holiday';
    else if (isSpecial) clickType = 'special';
    else if (isComp) clickType = 'comp';
    else if (empHols.length > 0) clickType = 'employee_holiday';

    daysGridHTML += `
      <div style="${bgStyle} border-radius:6px; cursor:pointer; min-height: 100px; display:flex; flex-direction:column; overflow:hidden;" onclick="showDayEventAction('${dateStr}', '${clickType}')" class="monthly-day-cell">
        ${cellContent}
        ${eventContent}
      </div>
    `;
  }

  daysGridHTML += `</div>`;
  gridHTML += daysGridHTML;
  gridHTML += `</div></div>`;

  container.innerHTML = gridHTML;
}

function showDayEventAction(dateStr, type) {
  const isEmpSelected = state.activeCompanySettings && state.activeCompanySettings.holidaySystem === 'employee_selected';
  if (isEmpSelected) {
    const isAdmin = window.checkPermission('manage_holidays');
    if (isAdmin && (type === 'holiday' || type === 'special' || type === 'comp')) {
      showEditHolidayModal(dateStr, type);
    } else {
      showEmployeeHolidayBookingModal(dateStr);
    }
    return;
  }

  if (type === 'none') {
    if (window.checkPermission('manage_holidays')) {
      showAddHolidayModal(dateStr);
    }
  } else if (type === 'employee_holiday') {
    const empHols = state.employeeHolidays.filter(eh => eh.date === dateStr);
    const empNames = empHols.map(eh => eh.employeeName).join(', ');
    alert(`📅 วันที่: ${new Date(dateStr).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })}\nประเภท: วันหยุดพนักงาน (เลือกส่วนตัว)\nรายชื่อพนักงานที่หยุด: ${empNames}`);
  } else {
    if (window.checkPermission('manage_holidays')) {
      showEditHolidayModal(dateStr, type);
    } else {
      alert(`📅 วันที่: ${new Date(dateStr).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })}\nประเภท: ${type === 'holiday' ? 'วันหยุดบริษัท' : (type === 'special' ? 'งานพิเศษ' : 'วันหยุดชดเชยพิเศษ')}`);
    }
  }
};

function showEmployeeHolidayBookingModal(dateStr) {
  let modalOverlay = document.getElementById('holiday-booking-modal-overlay');
  if (!modalOverlay) {
    modalOverlay = document.createElement('div');
    modalOverlay.id = 'holiday-booking-modal-overlay';
    modalOverlay.className = 'modal-overlay';
    document.body.appendChild(modalOverlay);
  }

  const selectedDate = new Date(dateStr + 'T00:00:00');
  const thaiDateStr = selectedDate.toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' });
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isFuture = selectedDate >= today;

  const isAdmin = window.checkPermission('manage_holidays');
  const isSuperAdmin = state.currentUser.id === 'emp_master' || state.currentUser.role === 'superadmin';
  const currentEmpId = state.currentUser.id;

  modalOverlay.innerHTML = `
    <div class="modal-container" style="max-width: 500px;">
      <div class="modal-header">
        <h3>📅 จัดการวันหยุดประจำปีเลือกเอง</h3>
        <button class="btn-close-modal" onclick="document.getElementById('holiday-booking-modal-overlay').classList.remove('active')">&times;</button>
      </div>
      <div class="modal-body" style="padding-top: 10px;">
        <p style="font-size: 1.05rem; margin-bottom: 15px;"><strong>วันที่เลือก:</strong> <span style="color: var(--primary-dark); font-weight:700;">${thaiDateStr}</span></p>
        
        <div class="form-group" id="booking-employee-select-container">
          <!-- Will render employee select here -->
        </div>

        <div id="booking-info-container" style="margin: 15px 0; padding: 15px; border-radius: var(--border-radius-sm); background: var(--gray-100); border: 1px solid var(--gray-300);">
          <!-- Dynamic info -->
        </div>

        <div style="display: flex; gap: 10px; margin-top: 20px;" id="booking-actions-container">
          <!-- Dynamic action buttons -->
        </div>
      </div>
    </div>
  `;
  const updateBookingModalForEmployee = function (empId) {
    const emp = state.employees.find(e => e.id === empId);
    if (!emp) return;

    const empName = emp.nickname || (emp.realName ? emp.realName.split(' ')[0] : 'พนักงาน');
    const quota = state.activeCompanySettings.companyHolidaysQuota !== undefined ? state.activeCompanySettings.companyHolidaysQuota : 13;

    const bookedHolidays = state.employeeHolidays.filter(eh => eh.employeeId === empId);
    const bookedCount = bookedHolidays.length;
    const isAlreadyBooked = bookedHolidays.some(eh => eh.date === dateStr);

    const infoBox = document.getElementById('booking-info-container');
    const actionsBox = document.getElementById('booking-actions-container');

    let infoHTML = `
      <div style="display:flex; flex-direction:column; gap:8px; font-size:0.95rem;">
        <div style="display:flex; justify-content:space-between;">
          <span>👤 ชื่อพนักงาน:</span>
          <strong>${escapeHtml(emp.realName)} (${escapeHtml(empName)})</strong>
        </div>
        <div style="display:flex; justify-content:space-between;">
          <span>📊 สิทธิ์วันหยุดประจำปีบริษัท:</span>
          <strong>${quota} วัน</strong>
        </div>
        <div style="display:flex; justify-content:space-between;">
          <span>💜 ใช้ไปแล้วในปฏิทินปีนี้:</span>
          <strong style="color: var(--primary-dark);">${bookedCount} วัน</strong>
        </div>
        <div style="display:flex; justify-content:space-between;">
          <span>⏳ โควตาคงเหลือ:</span>
          <strong style="color: ${quota - bookedCount > 0 ? 'var(--success)' : 'var(--danger)'};">${Math.max(0, quota - bookedCount)} วัน</strong>
        </div>
        <div style="display:flex; justify-content:space-between; border-top:1px solid var(--gray-300); padding-top:8px; margin-top:5px;">
          <span>สถานะในวันนี้:</span>
          <strong>${isAlreadyBooked ? '<span style="color:var(--success);">💜 เลือกไว้เป็นวันหยุดแล้ว</span>' : '<span style="color:var(--text-sub);">ไม่ได้เลือกไว้</span>'}</strong>
        </div>
      </div>
    `;
    infoBox.innerHTML = infoHTML;

    let actionsHTML = '';
    if (isAlreadyBooked) {
      if (isFuture || isSuperAdmin) {
        actionsHTML = `
          <button class="btn btn-danger" onclick="cancelEmployeeHolidayTrigger('${empId}', '${dateStr}')" style="width:100%;">❌ ยกเลิกวันหยุดนี้ ${!isFuture ? '(สิทธิ์ Super Admin)' : ''}</button>
        `;
      } else {
        actionsHTML = `
          <button class="btn btn-secondary" disabled style="width:100%; cursor: not-allowed;">🔒 ยกเลิกไม่ได้ (วันผ่านไปแล้ว)</button>
        `;
      }
    } else {
      if (isFuture || isSuperAdmin) {
        if (bookedCount >= quota) {
          actionsHTML = `
            <div style="width:100%; text-align:center;">
              <span style="color:var(--danger); font-weight:700; font-size:0.9rem;">⚠️ โควตาวันหยุดเต็มแล้ว (${quota} วัน)</span>
              <button class="btn btn-secondary" disabled style="width:100%; margin-top:8px; cursor: not-allowed;">บันทึกไม่ได้ (โควตาเต็ม)</button>
            </div>
          `;
        } else {
          actionsHTML = `
            <button class="btn btn-primary" onclick="submitEmployeeHolidayBooking('${empId}', '${dateStr}')" style="width:100%;">💾 บันทึกเลือกวันหยุดนี้ ${!isFuture ? '(สิทธิ์ Super Admin)' : ''}</button>
          `;
        }
      } else {
        actionsHTML = `
          <button class="btn btn-secondary" disabled style="width:100%; cursor: not-allowed;">🔒 บันทึกย้อนหลังไม่ได้ (ผ่านวันที่นี้มาแล้ว)</button>
        `;
      }
    }
    actionsBox.innerHTML = actionsHTML;
  };
  if (typeof window !== 'undefined') {
    window.updateBookingModalForEmployee = updateBookingModalForEmployee;
  }

  const selectorContainer = document.getElementById('booking-employee-select-container');
  if (isAdmin) {
    let selectHTML = `
      <label for="booking-employee-select" style="font-weight:600; margin-bottom: 6px; display:block;">เลือกพนักงานที่ต้องการจัดการ *</label>
      <select id="booking-employee-select" style="padding: 10px 14px; font-size: 0.95rem; border-radius: var(--border-radius-md); border: 1px solid var(--gray-300); outline: none; background: var(--card-bg); width: 100%; color: var(--text-main); font-family: inherit; cursor:pointer;" onchange="updateBookingModalForEmployee(this.value)">
    `;
    state.employees.forEach(e => {
      selectHTML += `<option value="${e.id}" ${e.id === currentEmpId ? 'selected' : ''}>${e.realName || 'ไม่ระบุชื่อ'} (${e.nickname || (e.realName ? e.realName.split(' ')[0] : 'พนักงาน')})</option>`;
    });
    selectHTML += `</select>`;
    selectorContainer.innerHTML = selectHTML;

    const initialEmpId = document.getElementById('booking-employee-select').value;
    updateBookingModalForEmployee(initialEmpId);
  } else {
    selectorContainer.innerHTML = `
      <label style="font-weight:600; margin-bottom: 6px; display:block;">จัดการวันหยุดสำหรับ</label>
      <div style="padding: 10px 14px; background: var(--gray-100); border: 1px solid var(--gray-300); border-radius: var(--border-radius-md); font-weight:700;">
        ${state.currentUser.realName}
      </div>
    `;
    updateBookingModalForEmployee(currentEmpId);
  }

  modalOverlay.classList.add('active');
};

async function submitEmployeeHolidayBooking(empId, dateStr) {
  const monthStr = dateStr.slice(0, 7);
  const dayNum = parseInt(dateStr.split('-')[2]);

  try {
    const ts = await getTimesheet(empId, monthStr);
    if (ts.isLocked) {
      toast.warning("ไม่สามารถบันทึกได้ เนื่องจากงวดการคำนวณเดือนนี้ถูกล็อกแล้ว");
      return;
    }

    if (!ts.days[dayNum]) {
      ts.days[dayNum] = { status: 'work', lateMinutes: 0, earlyLeaveMinutes: 0, advance: 0 };
    }
    ts.days[dayNum].status = 'holiday_selected';

    const emp = state.employees.find(e => e.id === empId);
    ts.summary = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);

    await saveTimesheet(empId, monthStr, ts);

    toast.success("บันทึกวันหยุดประจำปีเลือกเองสำเร็จ!");

    const overlay = document.getElementById('holiday-booking-modal-overlay');
    if (overlay) overlay.classList.remove('active');

    await loadCompanyContext();
    navigateTo('calendar');
  } catch (err) {
    console.error("Error saving holiday booking:", err);
    toast.error("เกิดข้อผิดพลาดในการบันทึกวันหยุด: " + (err.message || ''));
  }
};

async function cancelEmployeeHolidayTrigger(empId, dateStr) {
  const monthStr = dateStr.slice(0, 7);
  const dayNum = parseInt(dateStr.split('-')[2]);

  try {
    const ts = await getTimesheet(empId, monthStr);
    if (ts.isLocked) {
      toast.warning("ไม่สามารถยกเลิกได้ เนื่องจากงวดการคำนวณเดือนนี้ถูกล็อกแล้ว");
      return;
    }

    const emp = state.employees.find(e => e.id === empId);
    const defaultStatus = getDefaultDayStatus(emp, dateStr);

    if (ts.days[dayNum]) {
      ts.days[dayNum].status = defaultStatus;
    }

    ts.summary = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);

    await saveTimesheet(empId, monthStr, ts);

    toast.info("ยกเลิกการจองวันหยุดสำเร็จ!");

    const overlay = document.getElementById('holiday-booking-modal-overlay');
    if (overlay) overlay.classList.remove('active');

    await loadCompanyContext();
    navigateTo('calendar');
  } catch (err) {
    console.error("Error canceling holiday booking:", err);
    toast.error("เกิดข้อผิดพลาดในการยกเลิกวันหยุด: " + (err.message || ''));
  }
};

function getDefaultDayStatus(emp, dateStr) {
  const dateObj = new Date(dateStr + 'T00:00:00');
  const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayOfWeekNames[dateObj.getDay()];

  let isDefaultDayOff = false;
  if (emp.weeklyDaysOff && emp.weeklyDaysOff.length > 0) {
    isDefaultDayOff = emp.weeklyDaysOff.includes(dateObj.getDay());
  } else {
    const workHoursStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, dayName);
    isDefaultDayOff = workHoursStr === '0' || workHoursStr.startsWith('OFF:');
  }

  const isCompanyHoliday = state.holidays.some(h => h.date === dateStr);

  if (isCompanyHoliday) return 'holiday';
  if (isDefaultDayOff) return 'day_off';
  return 'work';
}

function showAddHolidayModal(preselectedDate = '') {
  window.showHolidayFormModal(null, 'holiday', preselectedDate);
};

function showEditHolidayModal(dateStr, type) {
  let targetEvent = null;
  let eventType = type;

  if (type === 'holiday') {
    targetEvent = state.holidays.find(h => h.date === dateStr);
  } else if (type === 'special') {
    targetEvent = state.specialWorkdays.find(s => s.date === dateStr);
  } else if (type === 'comp') {
    targetEvent = state.specialWorkdays.find(s => s.compensatoryOffDate === dateStr);
    eventType = (targetEvent && targetEvent.isStandalone) ? 'comp' : 'special';
  }

  if (!targetEvent) return;

  window.showHolidayFormModal(targetEvent, eventType);
};

function showHolidayFormModal(originalEvent = null, activeType = 'holiday', preselectedDate = '') {
  let modalOverlay = document.getElementById('holiday-modal-overlay');
  if (!modalOverlay) {
    modalOverlay = document.createElement('div');
    modalOverlay.id = 'holiday-modal-overlay';
    modalOverlay.className = 'modal-overlay';
    document.body.appendChild(modalOverlay);
  }

  const isEdit = !!originalEvent;
  const dateValue = isEdit ? (originalEvent.isStandalone ? originalEvent.compensatoryOffDate : originalEvent.date) : (preselectedDate || `${state.currentCalendarYear}-01-01`);
  const cleanNameValue = isEdit ? originalEvent.name : '';
  const compOffValue = '';
  const selectedType = isEdit ? activeType : activeType;

  const origDate = isEdit ? originalEvent.date : '';
  const origType = isEdit ? activeType : '';

  modalOverlay.innerHTML = `
    <div class="modal-container" style="max-width: 550px;">
      <div class="modal-header">
        <h3>${isEdit ? 'แก้ไขวันสำคัญในปฏิทิน' : 'กำหนดวันสำคัญในปฏิทิน'}</h3>
        <button class="btn-close-modal" onclick="document.getElementById('holiday-modal-overlay').classList.remove('active')">&times;</button>
      </div>
      <div class="modal-body">
        <form id="holiday-add-form">
          <input type="hidden" id="holiday-orig-date" value="${origDate}">
          <input type="hidden" id="holiday-orig-type" value="${origType}">

          <div class="form-group">
            <label>ประเภทรายการสำคัญ *</label>
            <select id="holiday-type">
              <option value="holiday" ${selectedType === 'holiday' ? 'selected' : ''}>วันหยุดประจำปีของบริษัท</option>
              <option value="special" ${selectedType === 'special' ? 'selected' : ''}>วันทำงานพิเศษ</option>
              <option value="comp" ${selectedType === 'comp' ? 'selected' : ''}>วันหยุดชดเชย</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>ระบุวันที่ *</label>
            <input type="date" id="holiday-date" value="${dateValue}" required>
          </div>

          <div class="form-group">
            <label>รายละเอียด/ชื่อเรียกสำคัญ *</label>
            <input type="text" id="holiday-name" placeholder="เช่น วันแรงงาน หรือ วันหยุดชดเชย" value="${cleanNameValue}" required>
          </div>
        </form>
      </div>
      <div class="modal-footer" style="display:flex; justify-content:space-between; align-items:center;">
        <div>
          ${isEdit ? `<button type="button" class="btn btn-danger" onclick="deleteCalendarEventFromModalTrigger()">🗑️ ลบ</button>` : ''}
        </div>
        <div style="display:flex; gap: 8px;">
          <button type="button" class="btn btn-secondary" onclick="document.getElementById('holiday-modal-overlay').classList.remove('active')">ยกเลิก</button>
          <button type="button" class="btn btn-primary" onclick="submitCalendarEventTrigger()">💾 ${isEdit ? 'บันทึกการแก้ไข' : 'เพิ่มเข้าปฏิทิน'}</button>
        </div>
      </div>
    </div>
  `;

  modalOverlay.classList.add('active');

  // Prevent form enter key submit page refreshes
  const calForm = document.getElementById('holiday-add-form');
  if (calForm) {
    calForm.addEventListener('submit', (e) => {
      e.preventDefault();
      submitCalendarEventTrigger();
    });
  }
};

async function deleteCalendarEventFromModalTrigger() {
  if (!confirm("คุณต้องการลบวันสำคัญนี้ออกจากระบบใช่หรือไม่?")) return;

  const origDate = document.getElementById('holiday-orig-date').value;
  const origType = document.getElementById('holiday-orig-type').value;
  if (!origDate) return;

  let updatedHolidays = [...state.holidays];
  let updatedSpecial = [...state.specialWorkdays];

  if (origType === 'holiday') {
    updatedHolidays = updatedHolidays.filter(h => h.date !== origDate);
  } else if (origType === 'special') {
    updatedSpecial = updatedSpecial.filter(s => s.date !== origDate);
  } else if (origType === 'comp') {
    const parent = updatedSpecial.find(s => s.compensatoryOffDate === origDate);
    if (parent) {
      if (parent.isStandalone) {
        updatedSpecial = updatedSpecial.filter(s => s !== parent);
      } else {
        parent.compensatoryOffDate = null;
        parent.isCompensated = false;
      }
    }
  }

  const payload = {
    companyId: state.activeCompanyId,
    year: state.currentCalendarYear,
    holidays: updatedHolidays,
    specialWorkdays: updatedSpecial
  };

  try {
    await saveCalendar(state.activeCompanyId, state.currentCalendarYear, payload);
    state.holidays = updatedHolidays;
    state.specialWorkdays = updatedSpecial;
    document.getElementById('holiday-modal-overlay').classList.remove('active');

    const container = document.getElementById('calendar-render-box');
    if (container) {
      if (document.getElementById('monthly-month-selector')) {
        window.renderMonthlyCalendarGrid(container);
      } else {
        window.renderYearlyCalendarGrid(container);
      }
    }

    if (typeof updateCalendarSummaryCounters === 'function') {
      updateCalendarSummaryCounters();
    }
  } catch (err) {
    console.error(err);
    alert('เกิดข้อผิดพลาดในการลบข้อมูล');
  }
};

function toggleCompensatoryOffField(val) {
  // Deprecated
};

async function submitCalendarEventTrigger() {
  const form = document.getElementById('holiday-add-form');
  if (!form.reportValidity()) return;

  const type = document.getElementById('holiday-type').value;
  const date = document.getElementById('holiday-date').value;
  const name = document.getElementById('holiday-name').value.trim();

  const origDate = document.getElementById('holiday-orig-date').value;
  const origType = document.getElementById('holiday-orig-type').value;
  const isEdit = !!origDate;

  let updatedHolidays = [...state.holidays];
  let updatedSpecial = [...state.specialWorkdays];

  if (isEdit) {
    if (origType === 'holiday') {
      updatedHolidays = updatedHolidays.filter(h => h.date !== origDate);
    } else {
      updatedSpecial = updatedSpecial.filter(s => s.date !== origDate);
    }
  }

  if (type === 'holiday') {
    updatedHolidays.push({ date, name });
  } else if (type === 'special') {
    let oldComp = null;
    let oldCompStatus = false;
    if (isEdit && origType !== 'holiday') {
      const origSpecial = state.specialWorkdays.find(s => s.date === origDate);
      if (origSpecial && !origSpecial.isStandalone) {
        oldComp = origSpecial.compensatoryOffDate;
        oldCompStatus = origSpecial.isCompensated;
      }
    }
    updatedSpecial.push({ date, name, compensatoryOffDate: oldComp, isCompensated: oldCompStatus });
  } else if (type === 'comp') {
    updatedSpecial.push({ date: 'comp_' + date, name, compensatoryOffDate: date, isCompensated: true, isStandalone: true });
  }

  const payload = {
    companyId: state.activeCompanyId,
    year: state.currentCalendarYear,
    holidays: updatedHolidays,
    specialWorkdays: updatedSpecial
  };

  try {
    await saveCalendar(state.activeCompanyId, state.currentCalendarYear, payload);
    toast.success(isEdit ? "บันทึกการแก้ไขสำเร็จ!" : "บันทึกวันสำคัญเข้าปฏิทินสำเร็จ!");
    document.getElementById('holiday-modal-overlay').classList.remove('active');
    navigateTo('calendar');
  } catch (err) {
    console.error(err);
    toast.error("ไม่สามารถบันทึกได้: " + (err.message || ''));
  }
};

async function deleteCalendarEventTrigger(dateStr, type) {
  const confirmed = await showConfirm({
    title: 'ยืนยันลบวันสำคัญ',
    message: 'คุณแน่ใจว่าต้องการลบรายการวันสำคัญนี้ออกจากปฏิทิน? การลบจะไม่สามารถกู้คืนได้',
    confirmText: '🗑️ ลบรายการ',
    isDanger: true
  });
  if (!confirmed) return;

  let updatedHolidays = [...state.holidays];
  let updatedSpecial = [...state.specialWorkdays];

  if (type === 'holiday') {
    updatedHolidays = updatedHolidays.filter(h => h.date !== dateStr);
  } else if (type === 'special') {
    updatedSpecial = updatedSpecial.filter(s => s.date !== dateStr);
  } else if (type === 'comp') {
    // If we delete the comp-off record, we simply unlock the compensatory field on the parent special workday
    const parent = updatedSpecial.find(s => s.compensatoryOffDate === dateStr);
    if (parent) {
      if (parent.isStandalone) {
        updatedSpecial = updatedSpecial.filter(s => s !== parent);
      } else {
        parent.compensatoryOffDate = null;
        parent.isCompensated = false;
      }
    }
  }

  const payload = {
    companyId: state.activeCompanyId,
    year: state.currentCalendarYear,
    holidays: updatedHolidays,
    specialWorkdays: updatedSpecial
  };

  try {
    await saveCalendar(state.activeCompanyId, state.currentCalendarYear, payload);
    toast.success("ลบข้อมูลสำเร็จ!");
    navigateTo('calendar');
  } catch (err) {
    console.error(err);
    toast.error("ล้มเหลวในการลบข้อมูล: " + (err.message || ''));
  }
};


if (typeof window !== 'undefined') {
  window.renderCalendarPage = renderCalendarPage;
  window.updateCalendarYear = updateCalendarYear;
  window.switchCalendarMode = switchCalendarMode;
  window.updateCalendarStatsCounters = updateCalendarStatsCounters;
  window.renderRowCalendarList = renderRowCalendarList;
  window.renderYearlyCalendarGrid = renderYearlyCalendarGrid;
  window.changeMonthlyMonth = changeMonthlyMonth;
  window.fetchThaiPublicHolidays = fetchThaiPublicHolidays;
  window.renderMonthlyCalendarGrid = renderMonthlyCalendarGrid;
  window.showDayEventAction = showDayEventAction;
  window.showEmployeeHolidayBookingModal = showEmployeeHolidayBookingModal;
  window.submitEmployeeHolidayBooking = submitEmployeeHolidayBooking;
  window.cancelEmployeeHolidayTrigger = cancelEmployeeHolidayTrigger;
  window.getDefaultDayStatus = getDefaultDayStatus;
  window.showAddHolidayModal = showAddHolidayModal;
  window.showEditHolidayModal = showEditHolidayModal;
  window.showHolidayFormModal = showHolidayFormModal;
  window.deleteCalendarEventFromModalTrigger = deleteCalendarEventFromModalTrigger;
  window.toggleCompensatoryOffField = toggleCompensatoryOffField;
  window.submitCalendarEventTrigger = submitCalendarEventTrigger;
  window.deleteCalendarEventTrigger = deleteCalendarEventTrigger;
}

export {
  renderCalendarPage,
  updateCalendarYear,
  switchCalendarMode,
  updateCalendarStatsCounters,
  renderRowCalendarList,
  renderYearlyCalendarGrid,
  changeMonthlyMonth,
  fetchThaiPublicHolidays,
  renderMonthlyCalendarGrid,
  showDayEventAction,
  showEmployeeHolidayBookingModal,
  submitEmployeeHolidayBooking,
  cancelEmployeeHolidayTrigger,
  getDefaultDayStatus,
  showAddHolidayModal,
  showEditHolidayModal,
  showHolidayFormModal,
  deleteCalendarEventFromModalTrigger,
  toggleCompensatoryOffField,
  submitCalendarEventTrigger,
  deleteCalendarEventTrigger
};
